import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  smallBtn: { ...ui.btnSm },
  input: { ...s.input, padding: '10px', fontSize: '14px' },
  label: { ...s.label, fontSize: '13px', marginBottom: '6px' },
  section: { ...s.card, padding: '20px' },
  cell: { ...s.inputSm, width: '52px', padding: '5px', fontSize: '13px', textAlign: 'center' },
  th: { ...s.th, padding: '6px 8px', background: 'transparent', borderBottom: 'none', verticalAlign: 'bottom', whiteSpace: 'nowrap' },
  stickyL: { position: 'sticky', left: 0, background: colors.bgCard, zIndex: 2 },
}

const CFG_DEFAULT = { is_active: false, title: 'Kütüphane Gelişim Karnesi', subtitle: '' }

function slugKey(label, used) {
  let base = String(label || 'beceri').toLowerCase()
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'beceri'
  let key = base, n = 2
  while (used.includes(key)) { key = base + '_' + n; n++ }
  return key
}

function SmartLibrary() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [cfg, setCfg] = useState(CFG_DEFAULT)
  const [skills, setSkills] = useState([])
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => { if (selectedBrand) fetchAll() }, [selectedBrand?.id]) // eslint-disable-line

  function fetchAll() {
    setLoading(true)
    Promise.all([
      axios.get(`${API}/admin/brands/${selectedBrand.id}/library-config`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/admin/brands/${selectedBrand.id}/library-map`, { headers }).catch(() => ({ data: { skills: [], categories: [] } })),
    ]).then(([c, m]) => {
      setCfg({ ...CFG_DEFAULT, ...(c.data || {}) })
      setSkills((m.data?.skills || []).map(s => ({ skill_key: s.skill_key, label: s.label, info_text: s.info_text || '' })))
      setCats((m.data?.categories || []).map(x => ({ category_code: x.category_code, category_name: x.category_name || '', scores: { ...(x.scores || {}) } })))
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  function setC(k, v) { setCfg(c => ({ ...c, [k]: v })) }

  function addSkill() {
    setSkills(prev => [...prev, { skill_key: slugKey('Yeni Beceri', prev.map(s => s.skill_key)), label: 'Yeni Beceri', info_text: '' }])
  }
  function renameSkill(i, label) { setSkills(prev => prev.map((s, idx) => idx === i ? { ...s, label } : s)) }
  function setSkillInfo(i, info_text) { setSkills(prev => prev.map((s, idx) => idx === i ? { ...s, info_text } : s)) }
  function removeSkill(i) {
    const key = skills[i].skill_key
    setSkills(prev => prev.filter((_, idx) => idx !== i))
    setCats(prev => prev.map(c => { const sc = { ...c.scores }; delete sc[key]; return { ...c, scores: sc } }))
  }
  function addCategory() { setCats(prev => [...prev, { category_code: '', category_name: '', scores: {} }]) }
  function removeCategory(i) { setCats(prev => prev.filter((_, idx) => idx !== i)) }
  function setCatField(i, field, v) { setCats(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: v } : c)) }
  function setScore(i, key, v) {
    let n = parseInt(v, 10); if (isNaN(n)) n = 0; n = Math.max(0, Math.min(100, n))
    setCats(prev => prev.map((c, idx) => idx === i ? { ...c, scores: { ...c.scores, [key]: n } } : c))
  }

  async function handleSave() {
    setSaving(true)
    try {
      await axios.post(`${API}/admin/brands/${selectedBrand.id}/library-config`, cfg, { headers })
      await axios.post(`${API}/admin/brands/${selectedBrand.id}/library-map`, { skills, categories: cats }, { headers })
      setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      alert('Hata: ' + (e.response?.data?.error || e.message))
    }
    setSaving(false)
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, maxWidth: '360px', margin: '0 auto 16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )
  if (loading) return <p style={ui.emptyText}>Yükleniyor...</p>

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={s.pageTitle}>Smart Library — {selectedBrand.name}</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.is_active} onChange={e => setC('is_active', e.target.checked)} style={{ accentColor: colors.primary }} />
          Aktif
        </label>
      </div>

      <div style={style.section}>
        <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={style.label}>Başlık</label>
            <input style={style.input} value={cfg.title} onChange={e => setC('title', e.target.value)} />
          </div>
          <div>
            <label style={style.label}>Alt başlık</label>
            <input style={style.input} value={cfg.subtitle} onChange={e => setC('subtitle', e.target.value)} />
          </div>
        </div>
      </div>

      {skills.length > 0 && (
        <div style={style.section}>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={s.sectionTitle}>Bilgi Mesajları</h3>
            <p style={s.sectionSubtitle}>
              Müşteri halkanın üzerine gelince (mobilde dokununca) görünen açıklama. Boş bırakılan becerilerde balon çıkmaz.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {skills.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="sx-break" style={{ fontSize: '13px', fontWeight: 500, minWidth: '190px', maxWidth: '100%', color: colors.textSecondary }}>{s.label}</span>
                <input
                  value={s.info_text || ''}
                  maxLength={400}
                  onChange={e => setSkillInfo(i, e.target.value)}
                  placeholder="Örn. Kitap okumak, çocuğun kelime dağarcığını ve anlatım gücünü geliştirir."
                  style={{ ...style.cell, flex: '1 1 220px', minWidth: 0, width: 'auto', maxWidth: '100%', textAlign: 'left' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={style.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h3 style={s.sectionTitle}>Beceri Haritası</h3>
            <p style={s.sectionSubtitle}>Kategori kodu (Txxx) ↔ beceri puanları (0-100). Bir kitabın puanı kategorilerinin maksimumudur.</p>
          </div>
          <div className="sx-wrap" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={style.smallBtn} onClick={addSkill}>+ Beceri</button>
            <button style={style.smallBtn} onClick={addCategory}>+ Kategori</button>
          </div>
        </div>

        {skills.length === 0 ? <p style={ui.emptyText}>Henüz beceri yok. "+ Beceri" ile ekle.</p> : (
          <div className="sx-scroll-x" style={{ overflowX: 'auto', maxWidth: '100%', border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
            <table className="sx-cards sx-cards-head" style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ ...style.th, ...style.stickyL, textAlign: 'left', minWidth: '220px', borderBottom: `1px solid ${colors.border}` }}>Kategori</th>
                  {skills.map((s, i) => (
                    <th key={i} style={{ ...style.th, borderBottom: `1px solid ${colors.border}` }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <input value={s.label} onChange={e => renameSkill(i, e.target.value)} style={{ ...style.cell, width: '92px', textAlign: 'left' }} />
                        <button onClick={() => removeSkill(i)} title="Beceriyi sil" style={{ ...ui.btnDangerQuiet, padding: '2px 6px' }}>×</button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cats.map((c, ci) => (
                  <tr key={ci}>
                    <td data-label="Kategori" style={{ ...style.stickyL, padding: '6px 8px', borderBottom: `1px solid ${colors.border}` }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input value={c.category_code} placeholder="Txxx" onChange={e => setCatField(ci, 'category_code', e.target.value)} style={{ ...style.cell, width: '64px', textAlign: 'left' }} />
                        <input value={c.category_name} placeholder="Kategori adı" onChange={e => setCatField(ci, 'category_name', e.target.value)} style={{ ...style.cell, width: '150px', textAlign: 'left' }} />
                        <button onClick={() => removeCategory(ci)} title="Kategoriyi sil" style={{ ...ui.btnDangerQuiet, padding: '2px 6px' }}>×</button>
                      </div>
                    </td>
                    {skills.map((s, si) => (
                      <td key={si} data-label={s.label} style={{ padding: '4px', textAlign: 'center', borderBottom: `1px solid ${colors.border}` }}>
                        <input type="number" min="0" max="100" value={c.scores[s.skill_key] ?? 0} onChange={e => setScore(ci, s.skill_key, e.target.value)} style={style.cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {saved && <p style={{ ...ui.notice, marginBottom: '12px' }}>✓ Kaydedildi</p>}
      <button onClick={handleSave} disabled={saving}
        style={{ ...s.btnPrimary, padding: '12px 32px', fontSize: '14px', opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </button>
    </div>
  )
}

export default SmartLibrary
